import { ContractT, SectionT } from './constants';

// Sections rendered in the agreement modal, in display order.
const AGREEMENT_SECTION_NAMES = [
  'schedule_of_contents',
  'borrower_summary',
  'account_opening_disclosures',
  'oec_terms',
  'waiver_of_jury_trial',
  'billing_rights',
  'arbitration_provision',
  'privacy_policy',
  'consent_to_electronic_signatures',
];
// Sections rendered in the standalone "authorizations" modal, in display order.
const AUTHORIZATIONS_SECTION_NAMES = [
  'authorizations_preamble',
  'consent_to_electronic_communications',
  'ach_authorization',
];

export const AUTHORIZATIONS_POSTSCRIPT_SECTION_NAME = 'authorizations_postscript';

const MINOR_WORDS = new Set(['a', 'an', 'and', 'of', 'or', 'the', 'to']);

// Title-cases each word, capitalizing the letter after a space or hyphen (e.g. "Open-End Line Of Credit").

const toTitleCase = (value: string): string => value
  .toLowerCase()
  .replace(/(^|[\s-])(\w+)/g, (_match, boundary, word, offset) => (
    offset > 0 && MINOR_WORDS.has(word)
      ? boundary + word
      : boundary + word.charAt(0).toUpperCase() + word.slice(1)
  ));

// Grabs the visible title (e.g. "OPEN-END LINE OF CREDIT AGREEMENT") from the schedule_of_contents markup.
const extractContractTitle = (html: string): string => toTitleCase(
  html.match(/class="contract-title"[^>]*>([^<]*)</)?.[1].trim() ?? '',
);

// Drops the contract-title heading from the markup once it has been promoted to the section name.
const removeContractTitle = (html: string): string => html.replace(/<h2[^>]*class="contract-title"[^>]*>[\s\S]*?<\/h2>/, '');

const pickSections = (all: SectionT[], names: string[]): SectionT[] => names
  .map((name) => all.find((section) => section.name === name))
  .filter((section): section is SectionT => !!section);

/**
 * Collapses a contract's many sections into the two the signing UI renders:
 * 1. the full agreement — the AGREEMENT_SECTION_NAMES html concatenated in order,
 *    named after the agreement's contract-title.
 * 2. `Authorizations` — the AUTHORIZATIONS_SECTION_NAMES html concatenated in order.
 * Sections in neither list are not rendered.
 */
export const buildContractSections = (
  contracts: ContractT[],
): SectionT[] => {
  const [contract] = contracts;

  if (!contract) return [];

  const agreement = pickSections(contract.sections, AGREEMENT_SECTION_NAMES);
  const authorizations = pickSections(contract.sections, AUTHORIZATIONS_SECTION_NAMES);

  const sections: SectionT[] = [];

  if (agreement.length > 0) {
    sections.push({
      html: removeContractTitle(agreement.map((section) => section.html).join('')),
      name: extractContractTitle(agreement[0].html),
    });
  }

  if (authorizations.length > 0) {
    sections.push({
      html: removeContractTitle(authorizations.map((section) => section.html).join('')),
      name: 'Authorizations',
    });
  }

  return sections;
};
